/**
 * Dependency-free district rules shared by the SPA, api/seo-render, the
 * sitemap and the loader, so every surface agrees on what a published
 * district is and what its URL looks like.
 */

/** A district page needs at least this many displays to be indexable or linked. */
export const DISTRICT_MIN_DISPLAY_COUNT = 8;

export type DistrictStatus = 'proposed' | 'published' | 'hidden';

/** Same grammar as geoSlug.SAFE_CITY_SLUG; kept here so api code can import without the geo helpers. */
export const SAFE_DISTRICT_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const toDistrictSlug = (name: string): string =>
  name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** /displays/in/texas/austin/siena. `stateSlug` is the lowercase full name the app already emits. */
export const buildDistrictPath = (stateSlug: string, citySlug: string, districtSlug: string): string =>
  `/displays/in/${encodeURIComponent(stateSlug)}/${encodeURIComponent(citySlug)}/${encodeURIComponent(districtSlug)}`;

/** Only published districts with enough displays are indexable, linked or in the sitemap. */
export const isDistrictPublic = (district: { status?: string | null; display_count?: number | null }): boolean =>
  district.status === 'published' && (district.display_count ?? 0) >= DISTRICT_MIN_DISPLAY_COUNT;

const DISTRICT_WORDING = {
  halloween: { heading: 'Halloween displays', description: 'Halloween displays' },
  christmas: { heading: 'Christmas lights', description: 'Christmas light displays' },
  neutral: { heading: 'Holiday light displays', description: 'holiday light displays' },
} as const;

/**
 * Copy for a district page: "Halloween displays in Siena, Round Rock TX",
 * "Christmas lights in ..." or neutral "Holiday light displays in ...".
 * `count` is the number of displays the chosen wording describes.
 */
export function districtCopy(
  name: string,
  city: string,
  stateCode: string,
  count: number,
  year: number,
  holiday: 'christmas' | 'halloween' | null = null,
) {
  const wording = DISTRICT_WORDING[holiday ?? 'neutral'];
  const where = `${name}, ${city} ${stateCode}`;
  const h1 = `${wording.heading} in ${where}`;
  const withSite = `${h1} | Light Hunt`;
  return {
    where,
    h1,
    title: withSite.length <= 65 ? withSite : h1,
    description: `Browse ${count} ${wording.description} in the ${name} area of ${city}, ${stateCode} for the ${year} season, with photos, a district map, and directions on Light Hunt.`,
    /** "Halloween displays" / "Christmas light displays" / "holiday light displays", for body sentences. */
    noun: wording.description,
  };
}

/** Postgres/PostgREST errors that mean "the districts migration is not applied yet". */
export const isMissingDistrictSchema = (error: { code?: string; message?: string } | null | undefined): boolean =>
  Boolean(error) &&
  (['42P01', '42703', 'PGRST200', 'PGRST204', 'PGRST205'].includes(error?.code ?? '') ||
    /light_district/i.test(error?.message ?? ''));
